import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ShieldCheck, Clock, MapPin, Droplets, CheckCircle2 } from 'lucide-react';
import Seo, { faqSchema } from '@/components/Seo';
import { CallLink, SectionHeading, HowItWorks, EmergencyCta, FaqSection, LeadForm } from '@/components/sections';
import { SERVICES, LOCATIONS, WHY_US, HOME_FAQS, IMAGES, PHONE_DISPLAY, ICON_MAP, BRAND } from '@/lib/site';

function Icon({ name, className }) {
  const C = ICON_MAP[name] || Droplets;
  return <C className={className} strokeWidth={1.9} />;
}

export default function HomePage() {
  const schema = useMemo(() => [
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: BRAND,
      url: typeof window !== 'undefined' ? window.location.origin : undefined,
      inLanguage: 'en-CA',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: BRAND,
      url: typeof window !== 'undefined' ? window.location.origin : undefined,
      telephone: '+1-778-557-2073',
      areaServed: [
        { '@type': 'City', name: 'Coquitlam' },
        { '@type': 'City', name: 'Port Coquitlam' },
        { '@type': 'City', name: 'Port Moody' },
      ],
      description: 'Tri-Cities water damage help serving Coquitlam, Port Coquitlam and Port Moody.',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Emergency Water Damage Help in Tri-Cities BC',
      serviceType: 'Water damage restoration help',
      provider: { '@type': 'Organization', name: BRAND },
      areaServed: ['Coquitlam', 'Port Coquitlam', 'Port Moody'],
    },
    faqSchema(HOME_FAQS),
  ], []);

  return (
    <>
      <Seo
        title="Water Damage Restoration Tri-Cities BC | 24/7 Emergency Help"
        description="Emergency water damage help in Coquitlam, Port Coquitlam and Port Moody for flooding, burst pipes, leaks, water extraction and drying."
        image={IMAGES.hero}
        schema={schema}
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-navy">
        <img src={IMAGES.hero} alt="Emergency water damage restoration response in a Tri-Cities BC home" className="absolute inset-0 h-full w-full object-cover opacity-50" loading="eager" fetchPriority="high" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-deep/90 via-navy-deep/75 to-navy/25" />
        <div className="container-page relative grid gap-10 py-14 lg:grid-cols-2 lg:items-center lg:py-20">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-water ring-1 ring-white/15">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ripple rounded-full bg-water" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-water" />
              </span>
              24/7 Water Damage Help
            </span>
            <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.05] text-white sm:text-5xl xl:text-6xl">
              Emergency Water Damage Help in <span className="text-water">Tri-Cities BC</span>
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-white/80">
              Flooded basement, burst pipe, leak, or standing water? Request help in Coquitlam, Port Coquitlam, or Port Moody and get connected with a local restoration professional.
            </p>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <CallLink label={`CALL ${PHONE_DISPLAY}`} source="home-hero" />
              <a href="#emergency-form" className="inline-flex items-center justify-center gap-1 rounded-full border border-white/25 px-6 py-3 text-sm font-semibold text-white transition hover:bg-white/10">
                Request Help Online <ChevronRight className="h-4 w-4" />
              </a>
            </div>
            <div className="mt-8 grid max-w-xl gap-3 text-sm text-white/75 sm:grid-cols-3">
              <span className="flex items-center gap-2"><Clock className="h-4 w-4 shrink-0 text-water" /> Fast local response</span>
              <span className="flex items-center gap-2"><MapPin className="h-4 w-4 shrink-0 text-water" /> Tri-Cities coverage</span>
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 shrink-0 text-water" /> Emergency help 24/7</span>
            </div>
          </div>
          <div id="emergency-form" className="animate-fade-up scroll-mt-24 lg:pl-6">
            <LeadForm source="home-hero" compact />
          </div>
        </div>
      </section>

      {/* Quick scan */}
      <section className="border-b border-border bg-white">
        <div className="container-page grid gap-3 py-5 sm:grid-cols-2 lg:grid-cols-4">
          {['Basement flooding', 'Burst pipes & leaks', 'Water extraction & drying', 'Mould & sewage concerns'].map((item) => (
            <div key={item} className="flex items-center gap-2 text-sm font-semibold text-primary">
              <CheckCircle2 className="h-4 w-4 shrink-0 text-water-strong" /> {item}
            </div>
          ))}
        </div>
      </section>

      {/* Services */}
      <section className="py-16 sm:py-20">
        <div className="container-page">
          <SectionHeading eyebrow="Water Damage Services" title="Help for the Most Common Water Emergencies" sub="Choose the situation closest to yours. If water is actively spreading, request help right away." />
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className="group rounded-2xl border border-border bg-white p-6 transition hover:-translate-y-1 hover:border-water/40 hover:shadow-xl hover:shadow-navy/5">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-water-light text-water-strong">
                  <Icon name={s.icon} className="h-6 w-6" />
                </span>
                <h3 className="mt-4 font-display text-lg font-bold text-primary">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.short}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-water-strong">
                  Service details <ChevronRight className="h-4 w-4 transition group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why choose us */}
      <section className="bg-secondary py-16 sm:py-20">
        <div className="container-page">
          <SectionHeading eyebrow="Why Tri-Cities Homeowners Choose Us" title="Fast Emergency Help When Every Minute Matters" sub="A simple way to request local water damage help without extra steps." />
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {WHY_US.slice(0, 4).map((w) => (
              <div key={w.title} className="flex gap-4 rounded-2xl bg-white p-6 ring-1 ring-border transition hover:-translate-y-0.5 hover:shadow-md hover:shadow-navy/5">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-navy-deep text-water">
                  <Icon name={w.icon} className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="font-display text-base font-bold text-primary">{w.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{w.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Areas served */}
      <section className="py-16 sm:py-20">
        <div className="container-page">
          <SectionHeading center eyebrow="Areas We Serve" title="One Tri-Cities Service Area" sub="Water damage help is available across Coquitlam, Port Coquitlam and Port Moody." />
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {LOCATIONS.map((l) => (
              <article key={l.slug} className="overflow-hidden rounded-2xl border border-border bg-white">
                <div className="relative h-52 overflow-hidden">
                  <img src={IMAGES.locations[l.slug]} alt={l.imageAlt} width="1200" height="720" className="h-full w-full object-cover" loading="lazy" decoding="async" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/80 via-navy-deep/15 to-transparent" />
                  <span className="absolute bottom-4 left-5 font-display text-2xl font-bold text-white">{l.name}</span>
                </div>
                <div className="p-5">
                  <p className="text-sm leading-relaxed text-muted-foreground">{l.description}</p>
                </div>
              </article>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to="/areas" className="inline-flex items-center gap-1 rounded-full border border-border bg-white px-6 py-3 text-sm font-semibold text-primary transition hover:border-water/40 hover:text-water-strong hover:shadow-md">
              View Service Areas <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
          <p className="mt-4 text-center text-[10px] leading-relaxed text-muted-foreground/70">
            Location photos: Coquitlam — Greg Salter (CC BY-SA 3.0); Port Coquitlam — Richard Eriksson (CC BY 2.0); Port Moody — Northwest (CC BY-SA 4.0), via Wikimedia Commons.
          </p>
        </div>
      </section>

      <HowItWorks />
      <FaqSection faqs={HOME_FAQS} />
      <EmergencyCta />

    </>
  );
}
